import { useState } from "react";
import moment from "moment-timezone";
import { useToast } from "@/components/ui/use-toast";
import {
  OtcReportDateRangeValidation,
  useGenerateInvoiceFeesReport,
} from "@/lib/api/otc";
import { CommonDestinationTimezone } from "@/lib/utils";
import { BearDatePicker } from "../ContractForm";
import { ReportCard, ReportDownloadLink } from "./ReportCard";

interface InvoiceFeesReportFilters {
  after_date?: Date;
  before_date?: Date;
}

/**
 * Invoice Fees Report — required after/before date range.
 */
export function InvoiceFeesReportCard() {
  const { toast } = useToast();
  const generateReport = useGenerateInvoiceFeesReport();
  const [filters, setFilters] = useState<InvoiceFeesReportFilters>({});
  const [link, setLink] = useState<string | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  const isDisabled = !filters.after_date || !filters.before_date;

  const onGenerate = async () => {
    if (isDownloading || isDisabled) return;
    setLink(undefined);
    setIsDownloading(true);

    try {
      const parsed = OtcReportDateRangeValidation.safeParse({
        after_date: filters.after_date
          ? moment.tz(filters.after_date, CommonDestinationTimezone).format("YYYY/MM/DD")
          : "",
        before_date: filters.before_date
          ? moment.tz(filters.before_date, CommonDestinationTimezone).format("YYYY/MM/DD")
          : "",
      });

      if (!parsed.success) {
        toast({ title: "Invalid Filters", description: "" });
        return;
      }

      const result = await generateReport(parsed.data);
      if (!result.success) {
        throw result;
      }
      setLink(result.data.link);
    } catch (error: unknown) {
      console.error("Failed to generate otc invoice fees report", error);
      toast({ title: "Failed to generate", description: "" });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ReportCard
      title="Invoice Fees Report"
      description=""
      isDownloading={isDownloading}
      isGenerateDisabled={isDisabled}
      onGenerate={onGenerate}
      hasResults={!!link}
      linkContent={link ? <ReportDownloadLink href={link} label="Download Report" /> : null}
    >
      <div className="grid grid-cols-1 gap-3">
        <BearDatePicker
          tabIndex={0}
          name="invoice_fees_report_after_date"
          label="After Date"
          value={filters.after_date}
          onValueChange={(date: Date | undefined) =>
            setFilters((prev: InvoiceFeesReportFilters) => ({ ...prev, after_date: date }))
          }
        />
        <BearDatePicker
          tabIndex={1}
          name="invoice_fees_report_before_date"
          label="Before Date"
          value={filters.before_date}
          onValueChange={(date: Date | undefined) =>
            setFilters((prev: InvoiceFeesReportFilters) => ({ ...prev, before_date: date }))
          }
        />
      </div>
    </ReportCard>
  );
}
